import { FC, useCallback, useState } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useStore } from '@hooks/useStore';
import CloseIcon from '@icons/close_icon.svg?component';
import DownloadIcon from '@icons/inputs/dowload_file_icon.svg?component';
import { ICataloguesPage } from '@stores/catalogues/types';
import { uploadInputTranslations } from '../UploadInput/translations';
import { UploadInputTransFields } from '../UploadInput/types';
import { IUploadJSONInputProps, IUploadJSONObject, UploadJSONInputTransFields } from './types';
import { uploadJSONInputTranslations } from './translations';
import { MAX_ID_LENGTH, MAX_PREVIEW_URL_LENGTH } from './constatnts';

const UploadJSONInput: FC<IUploadJSONInputProps> = ({
  name,
  inputStyleProp,
  onLoad,
  onReset,
  disabled,
}) => {
  const {
    alertStore: { showAlert },
  } = useStore();

  const [fileName, setFileName] = useState('');

  const { t } = useMyTranslations<UploadInputTransFields | UploadJSONInputTransFields>([
    uploadInputTranslations.name,
    uploadJSONInputTranslations.name,
  ]);

  const formContext = useFormContext();

  const { setValue } = formContext;

  const validateJSONPage = useCallback((page: any) => {
    return (
      typeof page === 'object' &&
      typeof page.id === 'string' &&
      typeof page.imageUrl === 'string' &&
      typeof page.position === 'number' &&
      page.position >= 0
    );
  }, []);

  const validateJSON = useCallback(
    (json: any) => {
      if (
        typeof json === 'object' &&
        typeof json.id === 'string' &&
        typeof json.previewUrl === 'string' &&
        typeof json.pages === 'object' &&
        json.id.length <= MAX_ID_LENGTH &&
        json.previewUrl.length <= MAX_PREVIEW_URL_LENGTH &&
        Array.isArray(json.pages) &&
        json.pages.every(validateJSONPage) &&
        new Set(json.pages.map(({ position }: ICataloguesPage) => position)).size ===
          json.pages.length
      ) {
        if (onLoad) onLoad(json);
        else setValue(name, json, { shouldDirty: true });
        return true;
      } else {
        showAlert(t('invalidJSON', { ns: uploadJSONInputTranslations.name }), 'warning');
        return false;
      }
    },
    [name, onLoad]
  );

  const onDrop = useCallback(async (acceptedFiles: any) => {
    if (disabled) return;
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      if (file.type !== 'application/json') {
        showAlert(t('incorrectFileType'), 'warning');
        return;
      }
      try {
        const jsonText = await file.text();
        if (validateJSON(JSON.parse(jsonText))) setFileName(file.name);
      } catch (e) {
        showAlert(t('cannotReadFile', { ns: uploadJSONInputTranslations.name }), 'warning');
      }
    }
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({ onDrop, noClick: true });

  const onResetHandler = useCallback(() => {
    if (onReset) onReset();
    else setValue(name, null);
    setFileName('');
  }, [name, onReset]);

  const getContent = useCallback(
    (value: IUploadJSONObject, error?: FieldError) => {
      if (value) {
        return (
          <>
            <p className="mb-[16px] small-text opacity-40">
              {t('uploadFileText', { ns: uploadJSONInputTranslations.name })}
            </p>
            {fileName ? (
              <div className="flex flex-col justify-center items-center gap-y-[16px] w-full p-[16px] bg-[#F3F5FC] rounded-[8px] relative">
                <img
                  src={value.previewUrl}
                  alt="point image"
                  className="object-contain w-[137px] h-[137px]"
                />
                <div className="flex items-center gap-x-[8px] w-full">
                  <p className="text-center ml-[8px]">{fileName}</p>
                  {!disabled && (
                    <button
                      className="ml-auto cursor-pointer bg-none bottom-[16px] right-[16px] rounded-[50%] transition-all hover:bg-[rgba(white, 0.3)]"
                      type="button"
                      onClick={onResetHandler}
                    >
                      <CloseIcon width={16} height={16} />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-[240px] w-[240px] p-[30px] bg-[#F3F5FC] rounded-[8px] relative">
                <img
                  src={value.previewUrl}
                  alt="point image"
                  className="h-full w-full object-contain"
                />
                {!disabled && (
                  <button
                    className="p-0 cursor-pointer absolute bg-none bottom-[16px] right-[16px] rounded-[50%] transition-all hover:bg-[rgba(white, 0.3)]"
                    type="button"
                    onClick={onResetHandler}
                    disabled={disabled}
                  >
                    <CloseIcon width={16} height={16} />
                  </button>
                )}
              </div>
            )}
          </>
        );
      } else {
        return (
          <div
            className={`h-[240px] w-[240px] rounded-[8px] bg-[rgba(246, 248, 255, 0.5)] border border-dashed border-[#C9CAD0] bg-[#F6F8FF80] flex flex-col justify-center items-center px-[12px] text-center ${
              error && 'border-error'
            }`}
          >
            <DownloadIcon width={48} height={48} />
            <p className="small-text uppercase mt-[16px]">{t('firstRow')}</p>
            <p className="small-text uppercase mt-[8px]">
              <span>{t('secondRow')}&nbsp; </span>
              <button
                type="button"
                onClick={open}
                className="cursor-pointer p-0 bg-none uppercase text-primary"
                disabled={disabled}
              >
                {t('btnText')}
              </button>
            </p>
            <p className="small-text opacity-40 mt-[8px]">{t('lastRow')}: JSON</p>
          </div>
        );
      }
    },
    [fileName]
  );

  return (
    <div className={`flex flex-col justify-center items-start w-full `} {...getRootProps()}>
      <Controller
        control={formContext?.control}
        name={name}
        rules={{ required: true }}
        render={({ field: { value }, fieldState: { error } }) => (
          <>
            <input
              {...getInputProps()}
              className={`opacity-0 w-0 h-0 overflow-hidden absolute -z-10 ${inputStyleProp}`}
              type="file"
              id={name}
              accept=".json"
              required
            />
            {getContent(value, error)}
          </>
        )}
      />
    </div>
  );
};

export default UploadJSONInput;
